import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Minus, Trash, Money, Bank, ArrowRight, ArrowsClockwise, Copy, CheckCircle, CircleNotch, X, CaretDown, Check, MagnifyingGlass } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND, useCapabilities } from '_helpers';
import { PageHeader, Bezel, Button, Input, IconButton, Modal, LoadingScreen, cn } from '_ui';

const emptyLine = () => ({ serviceId: '', quantity: 1 });
const todayISO = () => new Date().toISOString().slice(0, 10);

export function TaoHoaDon() {
    // Employees get their own tier regardless of the query; the ?tier hint only affects managers,
    // keeping their own-sale picker to one copy (they earn no commission, so Chính thức is fine).
    const { data: services, loading } = useFetch('/api/services?tier=chinh_thuc');
    const api = useApi();
    const alert = useAlertActions();
    const caps = useCapabilities();

    const [items, setItems] = useState([emptyLine()]);
    const [customerName, setCustomerName] = useState('');
    const [transactionDate, setTransactionDate] = useState(todayISO);
    const [method, setMethod] = useState('transfer');
    const [submitting, setSubmitting] = useState(false);
    const [receipt, setReceipt] = useState(null);

    const svcById = useMemo(() => {
        const m = new Map();
        (services || []).forEach(s => m.set(String(s._id), s));
        return m;
    }, [services]);

    const options = (services || []).map(s => ({ value: s._id, name: s.name, price: s.price }));

    const lines = items.map(it => {
        const svc = svcById.get(String(it.serviceId));
        const qty = it.quantity || 1;
        return {
            ...it,
            svc,
            lineTotal: svc ? svc.price * qty : 0,
        };
    });
    const totalPrice = lines.reduce((s, l) => s + l.lineTotal, 0);
    const validLines = items.filter(it => it.serviceId);

    function setLine(i, patch) {
        setItems(prev => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    }
    function addLine() { setItems(prev => [...prev, emptyLine()]); }
    function removeLine(i) { setItems(prev => (prev.length === 1 ? [emptyLine()] : prev.filter((_, idx) => idx !== i))); }

    async function submit() {
        if (validLines.length === 0) { alert.error('Chưa chọn dịch vụ', 'Hãy chọn ít nhất một dịch vụ.'); return; }
        setSubmitting(true);
        const body = {
            paymentMethod: method,
            customerName: customerName.trim(),
            items: validLines.map(it => ({ serviceId: it.serviceId, quantity: Number(it.quantity) || 1 })),
            // Only send transactionDate when actually backdating — otherwise the server should
            // stamp the real current time, not a forced midnight on "today".
            ...(transactionDate !== todayISO() && { transactionDate }),
        };
        const res = await api.post('/api/transactions', body);
        setSubmitting(false);
        if (res.ok) {
            setReceipt(res.data);
            alert.success('Đã tạo hoá đơn', method === 'transfer' ? 'Đưa mã QR cho khách quét.' : 'Chờ quản lý duyệt tiền mặt.');
        } else {
            alert.error('Không tạo được hoá đơn', res.error);
        }
    }

    function reset() {
        setReceipt(null);
        setItems([emptyLine()]);
        setCustomerName('');
        setTransactionDate(todayISO());
    }

    if (loading) return <LoadingScreen />;

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Nhân viên"
                title="Tạo hoá đơn"
                description="Chọn dịch vụ, nhập số lượng, rồi tạo mã thu tiền cho khách."
            />

            {receipt ? (
                <Receipt invoice={receipt} onNew={reset} alert={alert} />
            ) : (
                <div className="grid gap-8 lg:grid-cols-5">
                    {/* Form */}
                    <div className="space-y-6 lg:col-span-3">
                        <Bezel className="space-y-5 p-6 sm:p-8">
                            <h3 className="font-serif text-2xl text-espresso">Dịch vụ</h3>
                            <div className="space-y-4">
                                {items.map((it, i) => (
                                    <LineRow
                                        key={i}
                                        line={lines[i]}
                                        options={options}
                                        onService={v => setLine(i, { serviceId: v })}
                                        onQty={q => setLine(i, { quantity: q })}
                                        onRemove={() => removeLine(i)}
                                    />
                                ))}
                            </div>
                            <button onClick={addLine}
                                className="group inline-flex items-center gap-2 text-sm font-medium text-sage-deep transition-colors hover:text-espresso">
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sage/15 transition-transform duration-300 ease-fluid group-hover:scale-105">
                                    <Plus size={15} weight="bold" />
                                </span>
                                Thêm dịch vụ
                            </button>
                        </Bezel>

                        <Bezel className="space-y-5 p-6 sm:p-8">
                            <h3 className="font-serif text-2xl text-espresso">Khách &amp; thanh toán</h3>
                            <Input label="Tên khách (tuỳ chọn)" placeholder="VD: Chị Lan" value={customerName} onChange={e => setCustomerName(e.target.value)} />
                            {caps.canBackdateTransaction && (
                                <Input label="Ngày giao dịch" type="date" value={transactionDate} max={todayISO()} onChange={e => setTransactionDate(e.target.value)} />
                            )}
                            <div>
                                <span className="mb-1.5 block text-sm font-medium text-espresso-soft">Hình thức thanh toán</span>
                                <div className="grid grid-cols-2 gap-3">
                                    <MethodOption icon={Bank} label="Chuyển khoản" hint="Khách quét mã QR" active={method === 'transfer'} onClick={() => setMethod('transfer')} />
                                    <MethodOption icon={Money} label="Tiền mặt" hint="Quản lý duyệt cuối ngày" active={method === 'cash'} onClick={() => setMethod('cash')} />
                                </div>
                            </div>
                        </Bezel>
                    </div>

                    {/* Live summary */}
                    <div className="lg:col-span-2">
                        <div className="lg:sticky lg:top-14">
                            <Bezel className="p-6 sm:p-8">
                                <span className="text-[11px] font-semibold uppercase tracking-eyebrow text-espresso-soft/70">Tạm tính</span>
                                <div className="mt-5 space-y-3">
                                    {lines.filter(l => l.svc).length === 0 && (
                                        <p className="text-sm text-espresso-soft/60">Chưa có dịch vụ nào.</p>
                                    )}
                                    {lines.filter(l => l.svc).map((l, i) => (
                                        <div key={i} className="flex items-baseline justify-between gap-3 text-sm">
                                            <span className="text-espresso-soft">{l.svc.name} <span className="text-espresso-soft/50">×{l.quantity}</span></span>
                                            <span className="tnum text-espresso">{formatVND(l.lineTotal)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 space-y-2 border-t border-espresso/[0.08] pt-5">
                                    <Row label="Tổng tiền" value={formatVND(totalPrice)} big />
                                </div>
                                <Button onClick={submit} loading={submitting} icon={ArrowRight} size="lg" className="mt-7 w-full justify-between">
                                    {submitting ? 'Đang tạo…' : 'Tạo hoá đơn'}
                                </Button>
                            </Bezel>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function LineRow({ line, options, onService, onQty, onRemove }) {
    const qty = line.quantity || 1;
    const [pickerOpen, setPickerOpen] = useState(false);
    const selected = options.find(o => String(o.value) === String(line.serviceId));

    return (
        <div className="space-y-2.5">
            <button type="button" onClick={() => setPickerOpen(true)}
                className="flex w-full items-center justify-between rounded-2xl bg-cream-200/60 px-4 py-4 text-left ring-1 ring-espresso/10 transition-all duration-300 ease-fluid hover:bg-cream-200 active:scale-[0.99]">
                <span className={cn(selected ? 'font-medium text-espresso' : 'text-espresso-soft/50')}>
                    {selected ? `${selected.name} · ${formatVND(selected.price)}` : 'Chọn dịch vụ'}
                </span>
                <CaretDown size={16} weight="light" className="ml-2 shrink-0 text-espresso-soft" />
            </button>
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1 rounded-2xl bg-cream-200/60 p-1 ring-1 ring-espresso/10">
                    <IconButton icon={Minus} label="Giảm" variant="ghost" size={15} className="h-10 w-10" onClick={() => onQty(Math.max(1, qty - 1))} />
                    <span className="w-8 text-center text-sm font-semibold tnum">{qty}</span>
                    <IconButton icon={Plus} label="Tăng" variant="ghost" size={15} className="h-10 w-10" onClick={() => onQty(qty + 1)} />
                </div>
                <IconButton icon={Trash} label="Xoá dòng" variant="ghost" className="text-espresso-soft hover:text-danger" onClick={onRemove} />
            </div>

            <ServicePickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} options={options}
                selectedValue={line.serviceId} onSelect={v => { onService(v); setPickerOpen(false); }} />
        </div>
    );
}

// Full-screen-feeling picker (big tappable rows + search) — the compact dropdown was unusable on
// phones: cramped touch targets, and the options panel got clipped by the card's overflow.
function ServicePickerModal({ open, onClose, options, selectedValue, onSelect }) {
    const [query, setQuery] = useState('');
    const filtered = query.trim()
        ? options.filter(o => o.name.toLowerCase().includes(query.trim().toLowerCase()))
        : options;

    return (
        <Modal open={open} onClose={() => { setQuery(''); onClose(); }} eyebrow="Dịch vụ" title="Chọn dịch vụ" size="lg">
            <div className="relative mb-4">
                <MagnifyingGlass size={16} weight="light" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-espresso-soft/60" />
                <Input placeholder="Tìm dịch vụ…" value={query} onChange={e => setQuery(e.target.value)} className="pl-10" />
            </div>
            <div className="-mx-1 max-h-[55dvh] space-y-2 overflow-y-auto px-1">
                {filtered.length === 0 && <p className="py-10 text-center text-sm text-espresso-soft/60">Không tìm thấy dịch vụ.</p>}
                {filtered.map(o => {
                    const isSelected = String(o.value) === String(selectedValue);
                    return (
                        <button key={String(o.value)} type="button" onClick={() => onSelect(o.value)}
                            className={cn(
                                'flex w-full items-center justify-between rounded-2xl px-4 py-4 text-left ring-1 transition-colors',
                                isSelected ? 'bg-sage/10 ring-sage/40' : 'ring-espresso/10 hover:bg-espresso/[0.05] active:bg-espresso/[0.08]',
                            )}>
                            <span className="font-medium text-espresso">{o.name}</span>
                            <span className="flex items-center gap-2">
                                <span className="tnum text-espresso-soft">{formatVND(o.price)}</span>
                                {isSelected && <Check size={16} weight="bold" className="text-sage-deep" />}
                            </span>
                        </button>
                    );
                })}
            </div>
        </Modal>
    );
}

function MethodOption({ icon: Icon, label, hint, active, onClick }) {
    return (
        <button onClick={onClick}
            className={cn(
                'flex flex-col items-start gap-1 rounded-2xl p-4 text-left ring-1 transition-all duration-300 ease-fluid',
                active ? 'bg-espresso text-cream ring-espresso shadow-ambient' : 'bg-cream-200/50 text-espresso ring-espresso/10 hover:bg-espresso/[0.05]',
            )}>
            <Icon size={22} weight={active ? 'fill' : 'light'} />
            <span className="mt-1 font-medium">{label}</span>
            <span className={cn('text-xs', active ? 'text-cream/70' : 'text-espresso-soft/70')}>{hint}</span>
        </button>
    );
}

function Row({ label, value, big, accent }) {
    return (
        <div className="flex items-baseline justify-between">
            <span className={cn('text-sm', accent ? 'text-sage-deep' : 'text-espresso-soft')}>{label}</span>
            <span className={cn('tnum', big ? 'font-serif text-2xl text-espresso' : 'text-base font-semibold', accent && 'text-sage-deep')}>{value}</span>
        </div>
    );
}

// Post-submit receipt — QR + manual confirm for transfer, acknowledgement for cash.
function Receipt({ invoice, onNew, alert }) {
    const isTransfer = invoice.paymentMethod === 'transfer';
    const [paid, setPaid] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const api = useApi();

    async function confirm() {
        setConfirming(true);
        const res = await api.patch(`/api/transactions/${invoice._id}/confirm`);
        setConfirming(false);
        if (res.ok) setPaid(true);
        else alert.error('Không xác nhận được', res.error);
    }

    function copyCode() {
        if (navigator.clipboard && invoice.transferCode) {
            navigator.clipboard.writeText(invoice.transferCode).then(() => alert.success('Đã sao chép mã'));
        }
    }

    return (
        <div className="mx-auto max-w-xl">
            <Bezel className="p-8 sm:p-10">

                {/* ── Transfer: WAITING state ─────────────────────────────── */}
                {isTransfer && !paid && (
                    <>
                        <div className="flex items-center gap-2 text-espresso-soft/70">
                            <CircleNotch size={20} weight="bold" />
                            <span className="text-sm font-semibold">Chờ khách thanh toán</span>
                        </div>
                        <h3 className="mt-4 font-serif text-3xl text-espresso">Khách quét mã để thanh toán</h3>
                        <p className="mt-2 text-sm text-espresso-soft/80">Thấy tiền về rồi? Bấm xác nhận bên dưới.</p>
                        <div className="mt-7 flex flex-col items-center">
                            {invoice.qrPayload
                                ? <div className="rounded-2xl bg-paper p-4 shadow-ambient ring-1 ring-espresso/10">
                                    <QRCodeSVG value={invoice.qrPayload} size={232} level="M" fgColor="#2B2118" bgColor="#FFFFFF" />
                                  </div>
                                : <div className="flex h-64 w-64 items-center justify-center rounded-2xl bg-cream-200 px-6 text-center text-sm text-espresso-soft">Chưa cấu hình tài khoản nhận tiền (VIETQR_* trong .env)</div>}
                            {invoice.qrAccountName && (
                                <p className="mt-4 text-sm text-espresso-soft/80">Chuyển tới <span className="font-semibold text-espresso">{invoice.qrAccountName}</span>{invoice.qrAccountNo ? ` · ${invoice.qrAccountNo}` : ''}</p>
                            )}
                            <button onClick={copyCode} className="mt-3 inline-flex items-center gap-2 rounded-full bg-espresso/[0.05] px-4 py-2 text-sm font-medium text-espresso ring-1 ring-espresso/10 transition-colors hover:bg-espresso/10">
                                <span className="tnum tracking-wide">{invoice.transferCode}</span>
                                <Copy size={15} weight="light" />
                            </button>
                        </div>
                        <div className="mt-8 space-y-2 border-t border-espresso/[0.08] pt-6">
                            {invoice.customerName ? <Row label="Khách" value={invoice.customerName} /> : null}
                            <Row label="Tổng tiền" value={formatVND(invoice.totalPrice)} big />
                        </div>
                        <div className="mt-8 flex gap-3">
                            <Button onClick={confirm} loading={confirming} icon={CheckCircle} className="flex-1 justify-center">Xác nhận đã nhận tiền</Button>
                            <Button onClick={onNew} icon={X} variant="ghost" className="justify-center px-5">Huỷ</Button>
                        </div>
                    </>
                )}

                {/* ── Transfer: SUCCESS state ─────────────────────────────── */}
                {isTransfer && paid && (
                    <>
                        <div className="flex items-center gap-2 text-sage-deep">
                            <CheckCircle size={22} weight="fill" />
                            <span className="text-sm font-semibold">Thanh toán thành công</span>
                        </div>
                        <h3 className="mt-4 font-serif text-3xl text-espresso">Thanh toán thành công</h3>
                        <p className="mt-2 text-sm text-espresso-soft/80">Tiền đã về tài khoản.</p>
                        <div className="mt-8 space-y-2 border-t border-espresso/[0.08] pt-6">
                            {invoice.customerName ? <Row label="Khách" value={invoice.customerName} /> : null}
                            <Row label="Tổng tiền" value={formatVND(invoice.totalPrice)} big />
                        </div>
                        <Button onClick={onNew} icon={ArrowsClockwise} variant="ghost" className="mt-8 w-full justify-center">Tạo hoá đơn mới</Button>
                    </>
                )}

                {/* ── Cash: unchanged ─────────────────────────────────────── */}
                {!isTransfer && (
                    <>
                        <div className="flex items-center gap-2 text-sage-deep">
                            <CheckCircle size={22} weight="fill" />
                            <span className="text-sm font-semibold">Đã ghi nhận tiền mặt</span>
                        </div>
                        <h3 className="mt-4 font-serif text-3xl text-espresso">Đã ghi nhận tiền mặt</h3>
                        <p className="mt-2 text-sm text-espresso-soft/80">Quản lý sẽ duyệt tổng tiền mặt vào cuối ngày.</p>
                        <div className="mt-8 space-y-2 border-t border-espresso/[0.08] pt-6">
                            {invoice.customerName ? <Row label="Khách" value={invoice.customerName} /> : null}
                            <Row label="Tổng tiền" value={formatVND(invoice.totalPrice)} big />
                        </div>
                        <Button onClick={onNew} icon={ArrowsClockwise} variant="ghost" className="mt-8 w-full justify-center">Tạo hoá đơn mới</Button>
                    </>
                )}

            </Bezel>
        </div>
    );
}
